/**
 * Utility functions for handling user names
 */

/**
 * Construct a display name from separate name fields
 * @param {string} firstName - First name
 * @param {string} middleName - Middle name (optional)
 * @param {string} lastName - Last name
 * @returns {string} - Constructed display name
 */
export function constructDisplayName(firstName, middleName, lastName) {
  const nameParts = [firstName, middleName, lastName].filter(Boolean);
  return nameParts.join(' ');
}

/**
 * Get the first letter of a display name for avatars
 * @param {string} displayName - Display name
 * @returns {string} - First letter or fallback
 */
export function getDisplayNameInitial(displayName) {
  if (!displayName) return '?';
  return displayName[0].toUpperCase();
}

/**
 * Parse a display name into separate fields
 * @param {string} displayName - Full display name
 * @returns {object} - Object with firstName, middleName, lastName
 */
export function parseDisplayName(displayName) {
  if (!displayName) {
    return { firstName: '', middleName: '', lastName: '' };
  }
  
  const nameParts = displayName.trim().split(' ');
  
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], middleName: '', lastName: '' };
  } else if (nameParts.length === 2) {
    return { firstName: nameParts[0], middleName: '', lastName: nameParts[1] };
  } else {
    // 3 or more parts: first, middle(s), last
    return {
      firstName: nameParts[0],
      middleName: nameParts.slice(1, -1).join(' '),
      lastName: nameParts[nameParts.length - 1]
    };
  }
}

/**
 * Get user's full name for display, with fallback to email
 * @param {object} userData - User data object
 * @returns {string} - Display name or email fallback
 */
export function getUserDisplayName(userData) {
  if (!userData) return 'Unknown User';
  
  // If we have separate name fields, construct display name
  if (userData.firstName || userData.middleName || userData.lastName) {
    const displayName = constructDisplayName(userData.firstName, userData.middleName, userData.lastName);
    if (displayName.trim()) return displayName;
  }
  
  // Fallback to existing displayName or email
  return userData.displayName || userData.email || 'Unknown User';
}
